import { JSX, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  ProviderNetworkImportGrid,
  ProviderNetworkImportHeader,
  ProviderNetworkMissingStepsAlert,
} from ".";
import { QesIcons } from "@app/shared/icons/qesIcons";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import RoleRestricted from "@app/user/RoleRestricted";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { ProviderNetworkImportInfoPanel } from "@app/provider-network-imports/components/ProviderNetworkImportInfoPanel";
import { useVersions, useImportSaga, useImportResults, useImportConfig, useImportAddSteps, useImportSidePanel } from "@app/provider-network-imports/hooks";

/* oxlint-disable @typescript-eslint/no-unused-vars */
export const ProviderNetworkImportPage = (): JSX.Element => {
  const { loadVersions } = useVersions();
  const { loadSagaState } = useImportSaga();
  const { loadResults, isResultsLoading } = useImportResults();
  const { loadConfiguration, isConfigurationLoading } = useImportConfig();
  const { setDisplayAddSteps, displayAddSteps } = useImportAddSteps();
  const { closeSidePanel, openSidePanelResults, sidePanel } = useImportSidePanel();
  const { versionId, networkId } = useParams<{ networkId?: string, versionId?: string }>();
  const { search } = useLocation<{ search: string }>();

  useEffect(() => {
    loadResults(+versionId);
    loadSagaState(+versionId);
  }, [versionId]);

  useEffect(() => {
    loadConfiguration(+networkId);
    loadVersions(+networkId);
  }, [networkId]);

  // commenting out to prevent errors and we don't use this section of the codebase anyway
  // useEffect(() => {
  //   const showAddStepsPanel = "addSteps" in queryString.parse(search);
  //   if (showAddStepsPanel !== displayAddSteps) { setDisplayAddSteps(showAddStepsPanel); }

  //   const sidePanelStep = queryString.parse(search).step;
  //   const sidePanelTab = queryString.parse(search).tab;
  //   if (sidePanelStep && sidePanelStep !== sidePanel.step || sidePanelTab && sidePanelTab !== sidePanelTab.tab) {
  //     openSidePanelResults(+versionId, sidePanelStep, sidePanelTab);
  //   }
  //   if (!sidePanelStep && sidePanel.isOpen) { closeSidePanel(); }
  // }, [search]);

  if (isConfigurationLoading || isResultsLoading) { return null; }

  return (
    <UserContext.Consumer>
      {(user) => (
        <RoleRestricted allowedRoles={[Roles.PROVIDER_NETWORK_IMPORT]}>
          <ErrorBoundary user={user}>
            <Page documentTitle="Provider Network Import">
              <Breadcrumbs items={[{ iconClass: QesIcons.UserV1, text: "Providers", }]} />
              <PageInner>
                <ProviderNetworkImportHeader />
                <PageSection>
                  <ProviderNetworkImportInfoPanel />
                  {/*
                    TODO: add back when we have configurable steps
                    <div className="d-flex w-100">
                      <ImportAddStepsSidePanel />
                      <QESButton qesTestId="provider-network-import-page_run-all" qesType="secondary" onClick={runAll} className="ms-auto" disabled={!saga.providerSource || isActivated(+versionId) || !isLatestActivated(+versionId)}>Run All</QESButton>
                    </div>
                    <ImportStepSidePanel />
                  */}
                  <ProviderNetworkMissingStepsAlert />
                  <ProviderNetworkImportGrid />
                </PageSection>
              </PageInner>
            </Page>
          </ErrorBoundary>
        </RoleRestricted>
      )}
    </UserContext.Consumer>
  );
};
